import { PlusIcon, TrashIcon } from '@heroicons/react/20/solid';

import { FadeSlideIn } from './Animations';
import { Button } from './Button';
import { cn } from './cn';

export interface KeyValuePair {
  key: string;
  value: string;
}

export interface KeyValueEditorProps {
  pairs: KeyValuePair[];
  onChange: (pairs: KeyValuePair[]) => void;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  addLabel?: string;
  disabled?: boolean;
  readOnly?: boolean;
  /** Hides the add button when false, for fixed key sets (default: true). */
  allowAdd?: boolean;
  /** Hides the remove button for pairs this returns false for (default: all pairs removable). */
  isPairRemovable?: (pair: KeyValuePair, index: number) => boolean;
  className?: string;
}

export const KeyValueEditor = ({
  pairs,
  onChange,
  keyPlaceholder = 'Key',
  valuePlaceholder = 'Value',
  addLabel = 'Add',
  disabled,
  readOnly,
  allowAdd = true,
  isPairRemovable,
  className,
}: KeyValueEditorProps) => {
  const handleAdd = () => {
    onChange([...pairs, { key: '', value: '' }]);
  };

  const handleRemove = (index: number) => {
    onChange(pairs.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, field: 'key' | 'value', newValue: string) => {
    const updated = pairs.map((pair, i) => (i === index ? { ...pair, [field]: newValue } : pair));
    onChange(updated);
  };

  // Read-only: render as a styled table, never the editing controls.
  if (readOnly) {
    if (pairs.length === 0) {
      return <div className={cn('text-xs text-text-muted', className)}>No data to display.</div>;
    }
    return (
      <div className={cn('overflow-hidden rounded-lg border border-border', className)}>
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/50">
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-text-muted uppercase tracking-wider">
                Key
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-text-muted uppercase tracking-wider">
                Value
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {pairs.map((pair, index) => (
              <tr key={index} className="bg-surface hover:bg-gray-50/50 transition-colors">
                <td className="px-3 py-2 font-mono font-medium text-text-primary">{pair.key}</td>
                <td className="px-3 py-2 font-mono text-text-secondary break-all">{pair.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Editable: render as input pairs
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Header row for edit mode */}
      {pairs.length > 0 && (
        <div className="flex items-center gap-2 px-1">
          <span className="flex-1 text-[11px] font-semibold text-text-muted uppercase tracking-wider">
            {keyPlaceholder}
          </span>
          <span className="flex-1 text-[11px] font-semibold text-text-muted uppercase tracking-wider">
            {valuePlaceholder}
          </span>
          <span className="w-8 shrink-0" />
        </div>
      )}
      {pairs.map((pair, index) => (
        <FadeSlideIn
          key={index}
          className="flex items-center gap-2"
          direction="down"
          offset={4}
          transition={{ duration: 0.15 }}
        >
          <input
            type="text"
            value={pair.key}
            onChange={(e) => handleChange(index, 'key', e.target.value)}
            placeholder={keyPlaceholder}
            disabled={disabled}
            className="flex-1 rounded-md border border-border-strong bg-surface px-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500 disabled:bg-surface-hover disabled:opacity-50"
          />
          <input
            type="text"
            value={pair.value}
            onChange={(e) => handleChange(index, 'value', e.target.value)}
            placeholder={valuePlaceholder}
            disabled={disabled}
            className="flex-1 rounded-md border border-border-strong bg-surface px-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500 disabled:bg-surface-hover disabled:opacity-50"
          />
          {isPairRemovable === undefined || isPairRemovable(pair, index) ? (
            <button
              onClick={() => handleRemove(index)}
              disabled={disabled}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-400 hover:text-danger-500 hover:bg-gray-100 transition-colors duration-200 cursor-pointer bg-transparent border-none disabled:opacity-50"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          ) : (
            <span className="h-8 w-8 shrink-0" />
          )}
        </FadeSlideIn>
      ))}
      {allowAdd && (
        <Button variant="ghost" size="sm" onClick={handleAdd} disabled={disabled} className="self-start">
          <PlusIcon className="h-4 w-4" />
          {addLabel}
        </Button>
      )}
    </div>
  );
};
